/**
 * SpeedmasterWarranty.Config.js
 * Shared configuration helper — reads SMT settings once, exposes getters.
 * Every module in this extension imports this instead of reading SC.Configuration directly.
 */
define('SpeedmasterWarranty.Config', [
    'SC.Configuration',
    'Utils'
], function SpeedmasterWarrantyConfigDef(
    Configuration,
    Utils
) {
    'use strict';

    /* ── private cache (populated on first call) ─────────────────── */
    var _cache = null;

    /**
     * Read all speedmasterwarranty.* keys from Configuration and cache them.
     * Safe to call multiple times — only reads once.
     * @returns {Object} config map
     */
    function _load() {
        if (_cache) {
            return _cache;
        }
        _cache = {
            enabled:                  !!Configuration.get('speedmasterwarranty.enable'),
            warrantyItemId:           Configuration.get('speedmasterwarranty.warrantyItemId') || '',
            percentage:               Number(Configuration.get('speedmasterwarranty.percentage')) || 10,
            termYears:                Number(Configuration.get('speedmasterwarranty.termYears')) || 3,
            manufacturerWarrantyMonths: Number(Configuration.get('speedmasterwarranty.manufacturerWarrantyMonths')) || 12,
            termLabel:                Configuration.get('speedmasterwarranty.termLabel') || '3-Year Extended Warranty',
            declineLabel:             Configuration.get('speedmasterwarranty.declineLabel') || 'No Extra Cover',
            eligibilityField:         Configuration.get('speedmasterwarranty.eligibilityField') || 'custitem_extendedwarranty',
            warrantyOptedField:       Configuration.get('speedmasterwarranty.warrantyOptedField') || 'custcol_warranty_opted',
            warrantyAmountField:      Configuration.get('speedmasterwarranty.warrantyAmountField') || 'custcol_warranty_amount',
            warrantyStartField:       Configuration.get('speedmasterwarranty.warrantyStartField') || 'custcol_warranty_start',
            warrantyEndField:         Configuration.get('speedmasterwarranty.warrantyEndField') || 'custcol_warranty_end',
            qtySelectorToHide:        Configuration.get('speedmasterwarranty.qtySelectorToHide') || '.cart-item-summary-item-list-actionable-container-qty'
        };
        return _cache;
    }

    /* ── helpers ──────────────────────────────────────────────────── */

    /**
     * Currency symbol for the current session.
     * @returns {string} e.g. "$", "A$", etc.
     */
    function getCurrencySymbol() {
        var currency = (typeof SC !== 'undefined' && SC.getSessionInfo && SC.getSessionInfo('currency')) || null;
        return (currency && currency.symbol) ? currency.symbol : '$';
    }

    /**
     * Check whether a cart line item is eligible for extended warranty.
     * Looks at item.extras[eligibilityField] — the checkbox on the item record.
     * @param {Object} lineItem  The item object from a cart line (line.item)
     * @returns {boolean}
     */
    function isItemEligible(lineItem) {
        var cfg = _load();
        if (!lineItem) {
            return false;
        }
        // Check extras first (standard cart line item structure)
        var fieldValue = null;
        if (lineItem.extras) {
            fieldValue = lineItem.extras[cfg.eligibilityField];
        }
        // Fallback: check directly on lineItem (some contexts differ)
        if (fieldValue == null) {
            fieldValue = lineItem[cfg.eligibilityField];
        }
        // NetSuite returns "T"/"F", true/false, "true"/"false", or truthy objects
        if (fieldValue === true || fieldValue === 'T' || fieldValue === 'true' || fieldValue === 'Y') {
            return true;
        }
        // Accept any truthy value that's not explicitly false-like
        if (fieldValue && fieldValue !== false && fieldValue !== 'F' && fieldValue !== 'false' && fieldValue !== 'N') {
            return true;
        }
        return false;
    }

    /**
     * Check whether a cart line is the warranty summary service item.
     * @param {Object} line  Cart line object
     * @returns {boolean}
     */
    function isWarrantyLine(line) {
        var cfg = _load();
        return line && line.item && line.item.internalid && line.item.internalid.toString() === cfg.warrantyItemId;
    }

    /**
     * Find the warranty summary line in an array of cart lines.
     * @param {Array} lines
     * @returns {Object|null}
     */
    function findWarrantyLine(lines) {
        var cfg = _load();
        if (!lines || !lines.length) {
            return null;
        }
        var warrantyItemId = cfg.warrantyItemId;
        for (var i = 0; i < lines.length; i++) {
            if (lines[i].item && lines[i].item.internalid && lines[i].item.internalid.toString() === warrantyItemId) {
                return lines[i];
            }
        }
        return null;
    }

    /**
     * Calculate the warranty cost for a single line.
     * @param {number} lineAmount  The line's amount (price * qty)
     * @returns {number}  Warranty cost (2 decimal places)
     */
    function calcLineWarrantyCost(lineAmount) {
        var cfg = _load();
        var rate = cfg.percentage / 100;
        return Math.round(lineAmount * rate * 100) / 100;
    }

    /**
     * Format a number as currency using SCS Utils.
     * @param {number} amount
     * @returns {string}  e.g. "$10.00"
     */
    function formatPrice(amount) {
        return Utils.formatCurrency(amount, getCurrencySymbol());
    }

    /**
     * Calculate warranty start date = purchase date + manufacturer warranty months.
     * Returns ISO string (YYYY-MM-DD).
     * @param {Date} [purchaseDate]  Defaults to today
     * @returns {string}
     */
    function calcStartDate(purchaseDate) {
        var cfg = _load();
        var d = purchaseDate ? new Date(purchaseDate.getTime()) : new Date();
        d.setMonth(d.getMonth() + cfg.manufacturerWarrantyMonths);
        return _toISODate(d);
    }

    /**
     * Calculate warranty end date = start date + term years.
     * Returns ISO string (YYYY-MM-DD).
     * @param {string} startDateISO
     * @returns {string}
     */
    function calcEndDate(startDateISO) {
        var cfg = _load();
        var d = new Date(startDateISO);
        d.setFullYear(d.getFullYear() + cfg.termYears);
        return _toISODate(d);
    }

    /**
     * @private Format a Date as YYYY-MM-DD
     */
    function _toISODate(d) {
        var month = d.getMonth() + 1;
        var day = d.getDate();
        return d.getFullYear() + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
    }

    /* ── component references (set by Shopping.js mountToApp) ──── */
    var _pdpComponent = null;

    function setPdpComponent(pdp) { _pdpComponent = pdp; }
    function getPdpComponent() { return _pdpComponent; }

    /* ── per-item opt-in map (sessionStorage) ──────────────────── */
    // Shared across Shopping.js and PDP.View.js
    // Value: JSON object like {"itemId1": true, "itemId2": true}

    var ITEMS_KEY = 'sw_warranty_items';

    function getItemMap() {
        try {
            var raw = sessionStorage.getItem(ITEMS_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch (ex) {
            return {};
        }
    }

    function setItemOpted(itemId, opted) {
        var map = getItemMap();
        if (opted) {
            map[itemId] = true;
        } else {
            delete map[itemId];
        }
        try {
            sessionStorage.setItem(ITEMS_KEY, JSON.stringify(map));
        } catch (ex) {}
    }

    function isItemOpted(itemId) {
        return !!getItemMap()[itemId];
    }

    function clearItemMap() {
        try { sessionStorage.removeItem(ITEMS_KEY); } catch (ex) {}
    }

    function hasAnyOptedItems() {
        var map = getItemMap();
        for (var k in map) {
            if (map.hasOwnProperty(k) && map[k]) { return true; }
        }
        return false;
    }

    /* ── public API ──────────────────────────────────────────────── */
    return {
        get:                _load,
        getCurrencySymbol:  getCurrencySymbol,
        isItemEligible:     isItemEligible,
        isWarrantyLine:     isWarrantyLine,
        findWarrantyLine:   findWarrantyLine,
        calcLineWarrantyCost: calcLineWarrantyCost,
        formatPrice:        formatPrice,
        calcStartDate:      calcStartDate,
        calcEndDate:        calcEndDate,
        setPdpComponent:    setPdpComponent,
        getPdpComponent:    getPdpComponent,
        getItemMap:         getItemMap,
        setItemOpted:       setItemOpted,
        isItemOpted:        isItemOpted,
        clearItemMap:       clearItemMap,
        hasAnyOptedItems:   hasAnyOptedItems
    };
});
